import React, { useState, useEffect } from 'react';
import { motion, useSpring, useTransform } from 'framer-motion';
import '../styles/Home.css';
import DynamicSocialMediaCard from '../components/DynamicSocialMediaCard';
import SEOHead from '../components/SEOHead';
import CallToAction from '../components/CallToAction';
import WhyChooseUs from '../components/WhyChooseUs';
import OurPartnersSection from '../components/OurPartnersSection';
import ServicesSection from '../components/ServicesSection';
import TestimonialsSection from '../components/TestimonialsSection';
import BookingSection from '../components/BookingSection';

function Home() {
  // Use number type for scrollY
  const [scrollY, setScrollY] = useState(0);

  // Combine scroll handlers and scroll-to-top in one useEffect
  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Throttle scroll handler for better performance
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrollY(window.scrollY);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // SEO: Add structured data for homepage
  useEffect(() => {
    const homepageSchema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": "https://quantumframestudio.com/#webpage",
      "url": "https://quantumframestudio.com/",
      "name": "QuantumFrame Studio - Web Development & Digital Marketing Agency Bhopal",
      "isPartOf": {
        "@id": "https://quantumframestudio.com/#website"
      },
      "about": {
        "@type": "Organization",
        "name": "QuantumFrame Studio"
      },
      "description": "Leading web development and digital marketing agency in Bhopal. We create cutting-edge websites, SEO strategies, and digital solutions for business growth.",
      "breadcrumb": {
        "@id": "https://quantumframestudio.com/#breadcrumb"
      },
      "inLanguage": "en-IN",
      "potentialAction": [
        {
          "@type": "ReadAction",
          "target": ["https://quantumframestudio.com/"]
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.innerHTML = JSON.stringify(homepageSchema);
    document.head.appendChild(script);

    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.innerHTML.includes('WebPage')) {
        existingScript.remove();
      }
    };
  }, []);

  // Create spring animation with proper configuration
  const springConfig = { stiffness: 100, damping: 30, restDelta: 0.001 };
  const springScrollY = useSpring(scrollY, springConfig);

  // Smooth opacity and scale transforms
  const opacity = useTransform(
    springScrollY,
    [0, 200],
    [1, 0],
    { clamp: true }
  );

  const scale = useTransform(
    springScrollY,
    [0, 200],
    [1, 0.8],
    { clamp: true }
  );

  // Video ref for handling video loading
  const heroVideoRef = React.useRef(null);
  const backgroundVideoRef = React.useRef(null);

  // Handle video loading errors
  const handleVideoError = (e) => {
    console.error('Video loading error:', e);
    if (e.target) {
      e.target.style.display = 'none';
    }
  };

  // Ensure videos play on iOS devices and handle hero video containment
  useEffect(() => {
    const playVideoOnIOS = (videoRef) => {
      if (videoRef.current) {
        const video = videoRef.current;
        
        // Set required attributes for iOS
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.muted = true;
        video.autoplay = true;
        
        // Force play on iOS
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              // Video playing successfully
              console.log('Video autoplay started');
            })
            .catch((error) => {
              console.log('Video autoplay prevented:', error);
              // Autoplay was prevented, but video will still be background
            });
        }
      }
    };

    // Apply to all video refs
    playVideoOnIOS(heroVideoRef);
    playVideoOnIOS(backgroundVideoRef);

    // Handle hero video containment and background video visibility on mobile
    const handleMobileVideoSetup = () => {
      const isMobile = window.innerWidth <= 768;
      
      if (heroVideoRef.current && isMobile) {
        const heroSection = heroVideoRef.current.closest('.hero-section');
        const video = heroVideoRef.current;
        
        if (heroSection) {
          // Ensure hero video stays within hero section bounds
          const heroHeight = heroSection.offsetHeight;
          video.style.height = `${heroHeight}px`;
          video.style.maxHeight = `${heroHeight}px`;
          video.style.clip = `rect(0px, auto, ${heroHeight}px, 0px)`;
        }
      }
      
      // Ensure background video is visible on mobile
      if (backgroundVideoRef.current && isMobile) {
        const bgVideo = backgroundVideoRef.current;
        bgVideo.style.opacity = '1';
        bgVideo.style.display = 'block';
        
        // Force play for background video on mobile
        const playPromise = bgVideo.play();
        if (playPromise !== undefined) {
          playPromise.catch((error) => {
            console.log('Background video autoplay prevented on mobile:', error);
          });
        }
      }
    };

    // Apply mobile video setup on load and resize
    handleMobileVideoSetup();
    window.addEventListener('resize', handleMobileVideoSetup);

    return () => {
      window.removeEventListener('resize', handleMobileVideoSetup);
    };
  }, []);

  return (
    <div className="home-container">
      <SEOHead page="home" />
      
      {/* SEO-optimized hidden content for search engines */}
      <div style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', overflow: 'hidden' }}>
        <h1>QuantumFrame Studio - Leading Web Development Company in Bhopal, Madhya Pradesh</h1>
        <p>
          QuantumFrame Studio is the premier web development and digital marketing agency in Bhopal, Madhya Pradesh. 
          We are expert web developers specializing in custom website development, professional SEO services, 
          social media marketing, e-commerce development, and comprehensive digital marketing solutions. 
          Our team serves businesses across Bhopal, Indore, Jabalpur, Gwalior, and throughout Madhya Pradesh.
        </p>
        <h2>Best Web Development Services in Bhopal</h2>
        <p>
          As the top web development company in Bhopal, we offer custom website design, WordPress development, 
          React development, e-commerce solutions, and mobile-responsive web development. Our experienced 
          web developers create professional websites that drive business growth.
        </p>
        <h2>Digital Marketing Agency Bhopal - SEO Experts</h2>
        <p>
          Our digital marketing experts provide comprehensive SEO services, social media marketing, content marketing, 
          PPC advertising, and online reputation management. We help businesses in Bhopal and Madhya Pradesh 
          improve their online presence and search engine rankings.
        </p>
        <h2>Service Areas: Bhopal, Indore, Jabalpur, Madhya Pradesh</h2>
        <p>
          QuantumFrame Studio serves clients across Madhya Pradesh including Bhopal, Indore, Jabalpur, Gwalior, 
          Ujjain, Dewas, and surrounding areas. We provide local SEO services and help businesses rank higher 
          in local search results.
        </p>
      </div>

      <motion.section
        className="hero-section"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.5 }}
        style={{ opacity, scale }}
        role="banner"
        aria-label="Hero section showcasing QuantumFrame Studio services"
      >
        <video
          ref={heroVideoRef}
          autoPlay
          loop
          muted
          playsInline
          webkit-playsinline="true"
          preload="auto"
          className="background-video-heroes"
          onError={handleVideoError}
          aria-hidden="true"
          title="QuantumFrame Studio background video showcasing web development"
          style={{ pointerEvents: 'none' }}
        >
          <source src="/assets/video/heroes-back.mp4" type="video/mp4" />
          <source src="/assets/video/heroes-back.webm" type="video/webm" />
          Your browser does not support the video tag.
        </video>

        <motion.div
          className="hero-content"
          initial={{ x: -100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.5, duration: 1 }}
        >

          <motion.h1
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.8 }}
            role="heading"
            aria-level="1"
          >
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.4, duration: 0.5 }}
            >
              Transform Your Digital Presence with Expert
            </motion.span>
            <br />
            <motion.span
              className="highlight"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.6, duration: 0.5 }}
            >
              Web Development
            </motion.span>
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.8, duration: 0.5 }}
            >
              & Marketing Solutions
            </motion.span>
          </motion.h1>

          <motion.p
            className="hero-description"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 2, duration: 0.8 }}
            role="text"
            aria-label="Company description"
          >
            QuantumFrame Studio delivers cutting-edge website design, comprehensive SEO services, and strategic digital marketing campaigns in Bhopal. Our innovative approach combines modern web technologies with data-driven marketing strategies to accelerate your business growth and establish a commanding online presence.
          </motion.p>

          <motion.div
            className="hero-buttons"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 2.2, duration: 0.8 }}
            role="group"
            aria-label="Call to action buttons"
          >
            <motion.a
              href="/contact"
              className="cta-button primary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              role="button"
              aria-label="Get a free consultation for web development and digital marketing services"
              title="Free Consultation - Web Development & Digital Marketing in Bhopal"
            >
              Get a Free Consultation
            </motion.a>

            <motion.a
              href="/services"
              className="cta-button secondary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              role="button"
              aria-label="Explore our web development and digital marketing services"
              title="Our Services - Web Design, SEO, Digital Marketing in Bhopal"
            >
              Explore Our Services
            </motion.a>
          </motion.div>

          {/* SEO: Add hidden content for better keyword targeting */}
          <div className="sr-only" aria-hidden="true">
            <h2>Leading Web Development Company in Bhopal</h2>
            <p>
              QuantumFrame Studio is Bhopal's premier web development and digital marketing agency, 
              specializing in responsive website design, e-commerce development, SEO optimization, 
              social media marketing, and comprehensive digital solutions for businesses across 
              Madhya Pradesh and India.
            </p>
            <ul>
              <li>Custom Website Development Bhopal</li>
              <li>E-commerce Development Services</li>
              <li>SEO Services in Bhopal</li>
              <li>Digital Marketing Agency Bhopal</li>
              <li>Social Media Marketing</li>
              <li>Content Marketing Strategy</li>
              <li>UI/UX Design Services</li>
              <li>Mobile App Development</li>
            </ul>
          </div>
        </motion.div>

        <motion.div
          className="social-media-card-wrapper"
          initial={{ x: 100, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.5, duration: 1 }}
          role="complementary"
          aria-label="Social media showcase"
        >
          <DynamicSocialMediaCard />
        </motion.div>
      </motion.section>

      <section 
        className="additional-video-section"
        aria-label="Additional visual content"
      >
        <video
          ref={backgroundVideoRef}
          autoPlay
          loop
          muted
          playsInline
          webkit-playsinline="true"
          preload="auto"
          className="background-video"
          onError={handleVideoError}
          aria-hidden="true"
          title="QuantumFrame Studio portfolio showcase video"
          style={{ pointerEvents: 'none' }}
        >
          <source src="/assets/video/front-backgroundvid.mp4" type="video/mp4" />
          <source src="/assets/video/front-backgroundvid.webm" type="video/webm" />
          Your browser does not support the video tag.
        </video>
      </section>

      {/* Why Choose Us Section */}
      <WhyChooseUs />

      {/* Wrap other sections in React.Suspense if they're lazy loaded */}
      <CallToAction />
      <OurPartnersSection />
      <ServicesSection />
      <TestimonialsSection />
      <BookingSection />
    </div>
  );
}

export default Home;